import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { AgentPanel, useAgentChat, type AgentPanelClasses, type RenderInlineId } from '@avisoma/agent-ui';
import { Button } from '../../catalyst/button';
import { useSessionStorageState } from '../../hooks/useSessionStorageState';
import { IconArrowsPointingIn } from '../../icons/IconArrowsPointingIn';
import { IconArrowsPointingOut } from '../../icons/IconArrowsPointingOut';
import { DrawerResizeHandle } from './DrawerResizeHandle';
import {
  DEFAULT_DRAWER_WIDTH,
  browserLocalStorage,
  clampDrawerWidth,
  readDrawerWidth,
  writeDrawerWidth,
} from './drawerWidth';
import { EntityChip } from './EntityChip';
import { createProjectAgentSend } from './projectAgentSend';
import { useNarrowViewport, useViewportWidth } from './useNarrowViewport';

// Module-level, so the panel's markdown renderers stay stable across renders.
const renderInlineId: RenderInlineId = (id, kind) => <EntityChip id={id} kind={kind} />;

// Close to the acceptance questions in `server/playground/agent.test/questions.json`.
const STARTERS = [
  'Provide me with the installation/removal summary.',
  'How many ADs and SBs are outstanding on this engine?',
  'How often has this engine been in the shop, and what was performed each time?',
  'How many cycles are left before the next LLP limit, and which part limits it?',
  'Is there a clear thrust rating license position?',
  'List all inconsistencies in the records, even minor ones.',
];

const headerButtonClass =
  'rounded p-1 text-zinc-400 hover:text-zinc-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-zinc-400 dark:hover:text-zinc-200';

const classes: Partial<AgentPanelClasses> = {
  suggestionsHeading: 'text-xs font-medium text-zinc-500 dark:text-zinc-400',
  suggestions: 'mt-2 flex flex-wrap gap-2',
  suggestion:
    'rounded-full border border-zinc-950/10 px-3 py-1 text-left text-sm/5 text-zinc-700 hover:bg-zinc-950/2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-white/15 dark:text-zinc-300 dark:hover:bg-white/5',
};

export function ProjectAgentDrawer({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  // `useAgentChat` keys `submit` on `send`, so keep it stable per project.
  const options = useMemo(() => ({ send: createProjectAgentSend(projectId) }), [projectId]);
  const chat = useAgentChat(options);
  // Both survive the per-project remount (`ProjectView` keys the drawer on the project id).
  const [expanded, setExpanded] = useSessionStorageState('projectAgentDrawer.expanded', false);
  const [width, setWidth] = useState(() => {
    const storage = browserLocalStorage();
    return (storage && readDrawerWidth(storage)) ?? DEFAULT_DRAWER_WIDTH;
  });
  const [dragging, setDragging] = useState(false);
  const narrow = useNarrowViewport();
  const viewportWidth = useViewportWidth();

  if (!open) {
    // `bottom-20` keeps the toggle above the toast stack (`Toast` sits at `bottom-4 right-4`).
    return (
      <div className="fixed right-4 bottom-20 z-40">
        <Button className="shadow-lg" onClick={() => setOpen(true)} aria-expanded={false}>
          Ask assistant
        </Button>
      </div>
    );
  }

  const fullView = narrow || expanded;
  const sideWidth = clampDrawerWidth(width, viewportWidth);
  const expandLabel = expanded ? 'Collapse assistant' : 'Expand assistant';

  return (
    <aside
      aria-label="Project assistant"
      style={fullView ? undefined : { width: sideWidth }}
      className={clsx(
        'fixed z-30 flex flex-col bg-white dark:bg-zinc-900',
        fullView
          ? 'inset-0'
          : 'top-0 right-0 h-full border-l border-zinc-200 shadow-2xl dark:border-zinc-700 dark:shadow-zinc-950/30',
        dragging && 'select-none',
      )}
    >
      {!fullView && (
        <DrawerResizeHandle
          width={sideWidth}
          maxWidth={clampDrawerWidth(Infinity, viewportWidth)}
          onResize={(next) => setWidth(clampDrawerWidth(next, viewportWidth))}
          onResizeEnd={(next) => {
            const storage = browserLocalStorage();
            if (storage) writeDrawerWidth(storage, clampDrawerWidth(next, viewportWidth));
          }}
          onDraggingChange={setDragging}
        />
      )}
      <header className="flex items-center justify-between border-b border-zinc-200 px-3 py-2 dark:border-zinc-700">
        <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Project assistant</span>
        <div className="flex items-center gap-1">
          {!narrow && (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-label={expandLabel}
              aria-pressed={expanded}
              title={expandLabel}
              className={headerButtonClass}
            >
              {expanded ? <IconArrowsPointingIn /> : <IconArrowsPointingOut />}
            </button>
          )}
          <Button plain onClick={chat.reset}>
            New chat
          </Button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close assistant"
            className={headerButtonClass}
          >
            ✕
          </button>
        </div>
      </header>
      <div className={clsx('flex min-h-0 flex-1 flex-col', fullView && 'mx-auto w-full max-w-4xl')}>
        <AgentPanel chat={chat} renderInlineId={renderInlineId} starters={STARTERS} classes={classes} />
      </div>
    </aside>
  );
}
